/**
 * RfpContext — fetch the active tender's RFP row once per workspace mount.
 *
 * TenderWorkspace renders six tabs. Multiple panels (Brief, Eligibility,
 * Proposal, BoM) all need the same RfpOut payload. Before this provider
 * each panel did its own GET /rfp/{id}, which meant the first tender open
 * issued the same call three times in parallel and the Brief tab's render
 * flashed twice because the panels each owned a loading state.
 *
 * Use:
 *
 *   const { rfp, loading, error, refresh } = useRfp();
 *
 * The provider is mounted by TenderWorkspace.jsx and keyed off the
 * ``rfpId`` route param. ``refresh()`` is exposed for panels that need
 * to force a re-fetch (e.g. after a reclassify or a re-analyze action).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import { v2Api } from "@/lib/v2Api";

const RfpContext = createContext(null);


export function RfpProvider({ rfpId, children }) {
  const [rfp, setRfp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Section focus is driven by the SearchPanel: when a user clicks a
  // hit's #section chip we set this index AND its title, the Brief tab
  // listens and scrolls / expands the matching card. We carry both
  // because the v1 RFPAnalysis page renders sections from a separate
  // collection (db.rfp_sections) whose row IDs don't line up with the
  // v2 analysis.sections array — matching by title is the only
  // reliable bridge until Item 3c stage 2 ports that page off v1.
  // ``focusToken`` is bumped on every call so clicking the same
  // section twice still re-triggers the scroll.
  const [focusSectionIndex, setFocusSectionIndex] = useState(null);
  const [focusSectionTitle, setFocusSectionTitle] = useState(null);
  const [focusToken, setFocusToken] = useState(0);

  // Per-section review rows, keyed by section_index. Loaded once per
  // tender + replaced row-by-row when the SectionReviewBar dispatches.
  const [sectionReviews, setSectionReviews] = useState({});
  const setSectionReview = useCallback((idx, row) => {
    setSectionReviews((prev) => ({ ...prev, [idx]: row }));
  }, []);

  // Workspace departments — needed for name lookup in section cards.
  const [departments, setDepartments] = useState([]);

  // Current user's dept memberships — needed to decide if "Claim" is visible.
  const [myDeptMemberships, setMyDeptMemberships] = useState([]);

  // Decoded user_id + role from JWT token in localStorage.
  const { currentUserId, currentUserRole } = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return { currentUserId: null, currentUserRole: null };
      const payload = JSON.parse(atob(token.split(".")[1]));
      return {
        currentUserId: payload.sub || payload.user_id || null,
        currentUserRole: (payload.role || "").toLowerCase(),
      };
    } catch { return { currentUserId: null, currentUserRole: null }; }
  }, []);

  // Workspace role: admin or manager (not project lead — that is checked separately).
  const isManagerOrAdmin = useMemo(
    () => ["admin", "manager"].includes(currentUserRole),
    [currentUserRole],
  );

  // True when the logged-in user is the designated project lead for this RFP's project.
  const isProjectLead = useMemo(
    () => !!rfp?.project_lead_user_id && rfp.project_lead_user_id === currentUserId,
    [rfp?.project_lead_user_id, currentUserId],
  );

  // True when the logged-in user is dept lead in at least one department.
  const isDeptLead = useMemo(
    () => myDeptMemberships.some((m) => m.role === "lead"),
    [myDeptMemberships],
  );

  // Can drive the RFP workflow: admin, manager, or project lead of this project.
  const canManageRfp = useMemo(
    () => isManagerOrAdmin || isProjectLead,
    [isManagerOrAdmin, isProjectLead],
  );

  const refresh = useCallback(async () => {
    if (!rfpId) {
      setRfp(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.rfp.get(rfpId);
      setRfp(data);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Could not load tender");
      setRfp(null);
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Initial load of the review map; non-fatal if it fails. The
  // SectionReviewBar still works — it just won't pre-fill statuses
  // from the server. New reviews land via setSectionReview() on dispatch.
  useEffect(() => {
    if (!rfpId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await v2Api.rfp.listSectionReviews(rfpId);
        if (cancelled) return;
        const byIdx = {};
        for (const r of (Array.isArray(rows) ? rows : [])) {
          byIdx[r.section_index] = r;
        }
        setSectionReviews(byIdx);
      } catch {
        /* swallow — review state is advisory, not blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [rfpId]);

  // Load workspace departments once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const depts = await v2Api.departments();
        if (!cancelled) setDepartments(Array.isArray(depts) ? depts : []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load current user's dept memberships once.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await v2Api.departmentMembers.forUser(currentUserId);
        if (!cancelled) setMyDeptMemberships(Array.isArray(rows) ? rows : []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [currentUserId]);

  const focusSection = useCallback((idx, title = null) => {
    setFocusSectionIndex(idx);
    setFocusSectionTitle(title);
    setFocusToken((t) => t + 1);
  }, []);

  const value = useMemo(
    () => ({
      rfpId, rfp, loading, error, refresh,
      focusSectionIndex, focusSectionTitle, focusToken, focusSection,
      sectionReviews, setSectionReview,
      departments,
      myDeptMemberships,
      currentUserId,
      currentUserRole,
      isManagerOrAdmin,
      isProjectLead,
      isDeptLead,
      canManageRfp,
    }),
    [rfpId, rfp, loading, error, refresh,
     focusSectionIndex, focusSectionTitle, focusToken, focusSection,
     sectionReviews, setSectionReview,
     departments, myDeptMemberships, currentUserId, currentUserRole,
     isManagerOrAdmin, isProjectLead, isDeptLead, canManageRfp],
  );

  return <RfpContext.Provider value={value}>{children}</RfpContext.Provider>;
}


export function useRfp() {
  const ctx = useContext(RfpContext);
  if (ctx == null) {
    throw new Error("useRfp() must be called inside an <RfpProvider>");
  }
  return ctx;
}


/**
 * Like useRfp() but returns ``null`` when no provider is mounted.
 *
 * Use this from components that may be rendered both inside
 * TenderWorkspace (where the provider supplies a prefetched RFP) AND
 * standalone via a legacy route (where they still own the fetch).
 */
export function useRfpOptional() {
  return useContext(RfpContext);
}
